import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "@/components/ui/accordion";
import { api } from "@/lib/api";
import { useSEO } from "@/lib/useSEO";

const Wrap = ({ title, children }) => (
  <div className="px-4 sm:px-6 lg:px-8 max-w-3xl mx-auto py-14 sm:py-20">
    <h1 className="font-heading text-3xl sm:text-4xl font-extrabold tracking-tight text-slate-900 dark:text-white mb-8">
      {title}
    </h1>
    <div className="prose-sm max-w-none space-y-4 text-slate-600 dark:text-slate-300 leading-relaxed">
      {children}
    </div>
  </div>
);

const H2 = ({ children }) => (
  <h2 className="font-heading text-xl font-bold text-slate-900 dark:text-white mt-8 mb-2">
    {children}
  </h2>
);

export function HowItWorks() {
  useSEO("Cum funcționează | ActeSimplu", "Află cum generezi un document personalizat cu ActeSimplu în câțiva pași simpli.");
  const steps = [
    ["Alegi documentul", "Selectezi din catalog tipul de document de care ai nevoie."],
    ["Completezi câteva informații", "Răspunzi la un formular scurt și ghidat, în limba română."],
    ["Verifici datele", "Înainte de plată vezi toate informațiile introduse și le poți modifica."],
    ["Plătești 10 lei", "Plată unică, securizată prin Stripe. Fără cont și fără abonament."],
    ["Descarci documentul", "Primești documentul în format PDF și Word, gata de folosit."],
  ];
  return (
    <Wrap title="Cum funcționează">
      <p>Generarea unui document durează, de obicei, mai puțin de câteva minute.</p>
      <ol className="space-y-4 mt-4 list-none pl-0">
        {steps.map(([t, d], i) => (
          <li key={t} className="flex gap-4">
            <span className="inline-flex items-center justify-center w-8 h-8 rounded-full bg-blue-600 text-white font-bold shrink-0">
              {i + 1}
            </span>
            <div>
              <p className="font-semibold text-slate-900 dark:text-white">{t}</p>
              <p className="text-sm">{d}</p>
            </div>
          </li>
        ))}
      </ol>
      <p className="mt-6">
        <Link to="/#documente" className="text-blue-600 font-semibold">Vezi documentele disponibile →</Link>
      </p>
    </Wrap>
  );
}

export function FAQ() {
  useSEO("Întrebări frecvente | ActeSimplu", "Răspunsuri la cele mai frecvente întrebări despre ActeSimplu.");
  const faqs = [
    ["Am nevoie de cont pentru a genera un document?", "Nu. Poți genera un document fără să îți creezi cont. Ai nevoie doar de o adresă de email pentru a primi documentul."],
    ["Cât costă un document?", "Fiecare document costă 10 lei. Este o plată unică, fără abonament."],
    ["În ce format primesc documentul?", "Primești documentul în două formate: PDF și Word (DOCX)."],
    ["Cât timp am acces la document?", "Fișierele temporare sunt disponibile pentru descărcare timp de 72 de ore, apoi sunt șterse automat pentru protecția datelor."],
    ["Este plata securizată?", "Da. Plata este procesată de Stripe, un furnizor extern. Nu stocăm niciodată datele cardului tău."],
    ["Documentele constituie consultanță juridică?", "Nu. ActeSimplu generează modele de documente pe baza informațiilor introduse. Documentele nu constituie consultanță juridică, fiscală sau profesională."],
  ];
  return (
    <Wrap title="Întrebări frecvente">
      <Accordion type="single" collapsible className="w-full not-prose">
        {faqs.map(([q, a], i) => (
          <AccordionItem key={i} value={`f-${i}`}>
            <AccordionTrigger className="text-left font-medium text-slate-800 dark:text-slate-100">{q}</AccordionTrigger>
            <AccordionContent className="text-slate-600 dark:text-slate-300">{a}</AccordionContent>
          </AccordionItem>
        ))}
      </Accordion>
    </Wrap>
  );
}

export function Privacy() {
  useSEO("Politica de confidențialitate | ActeSimplu");
  return (
    <Wrap title="Politica de confidențialitate">
      <p>Ultima actualizare: {new Date().getFullYear()}</p>
      <H2>1. Ce date colectăm</H2>
      <p>Colectăm doar datele necesare generării documentului solicitat: informațiile pe care le introduci în formular și adresa ta de email pentru livrarea documentului. Nu colectăm date care nu sunt necesare.</p>
      <H2>2. Cum folosim datele</H2>
      <p>Datele introduse sunt folosite exclusiv pentru generarea documentului tău și pentru trimiterea acestuia pe email. Nu vindem și nu transmitem datele tale către terți în scopuri de marketing.</p>
      <H2>3. Plăți</H2>
      <p>Plățile sunt procesate de Stripe. Nu stocăm datele cardului tău în sistemele noastre.</p>
      <H2>4. Stocarea documentelor</H2>
      <p>Documentele generate sunt stocate temporar și șterse automat după 72 de ore. După acest interval, fișierele PDF și Word nu mai pot fi descărcate.</p>
      <H2>5. Drepturile tale</H2>
      <p>Ai dreptul de a solicita informații despre datele tale sau ștergerea acestora. Ne poți contacta prin <Link to="/contact" className="text-blue-600">pagina de contact</Link>.</p>
    </Wrap>
  );
}

export function Terms() {
  useSEO("Termeni și condiții | ActeSimplu");
  return (
    <Wrap title="Termeni și condiții">
      <H2>1. Serviciul</H2>
      <p>ActeSimplu este o platformă care generează modele de documente administrative personalizate pe baza informațiilor introduse de utilizator.</p>
      <H2>2. Preț și plată</H2>
      <p>Fiecare document costă 10 lei, plată unică. Prețul este afișat înainte de plată. Nu percepem abonamente.</p>
      <H2>3. Limitarea răspunderii</H2>
      <p>Documentele generate sunt modele și nu constituie consultanță juridică, fiscală sau profesională. ActeSimplu nu garantează un anumit rezultat juridic. Este responsabilitatea utilizatorului să verifice corectitudinea informațiilor înainte de utilizare.</p>
      <H2>4. Rambursări</H2>
      <p>Întrucât documentul este generat digital imediat după plată, rambursările se analizează de la caz la caz, în special în situația în care documentul nu a putut fi generat.</p>
      <H2>5. Utilizare corectă</H2>
      <p>Te obligi să folosești serviciul doar în scopuri legale și să introduci informații corecte și reale.</p>
    </Wrap>
  );
}

export function Cookie() {
  useSEO("Politica de cookie-uri | ActeSimplu");
  return (
    <Wrap title="Politica de cookie-uri">
      <p>ActeSimplu folosește un număr minim de cookie-uri și tehnologii similare.</p>
      <H2>Cookie-uri necesare</H2>
      <p>Folosim stocare locală în browser pentru a reține temporar comanda ta curentă, astfel încât să poți descărca documentul după plată. Aceste date rămân pe dispozitivul tău.</p>
      <H2>Analiză</H2>
      <p>Folosim statistici agregate și anonime pentru a înțelege cum este folosită platforma. Nu transmitem datele tale personale către servicii de analiză.</p>
    </Wrap>
  );
}

export function Contact() {
  useSEO("Contact | ActeSimplu", "Contactează echipa ActeSimplu.");
  return (
    <Wrap title="Contact">
      <p>Ai o întrebare sau o problemă cu o comandă? Suntem aici să te ajutăm.</p>
      <H2>Email</H2>
      <p>Ne poți scrie oricând, iar comenzile pot fi identificate prin ID-ul comenzii primit după plată.</p>
      <p className="mt-2 font-semibold text-slate-900 dark:text-white">contact@actesimplu.ro</p>
      <H2>Timp de răspuns</H2>
      <p>Răspundem, de regulă, în cel mult 1–2 zile lucrătoare.</p>
    </Wrap>
  );
}
